const post = {
  meta: {
    slug: "what-happens-after-you-prequalify",
    title: "What Happens After You Pre-Qualify for a Business Loan?",
    excerpt:
      "You clicked apply. You got a pre-approval email an hour later. What now? Here's the exact step-by-step of what happens next — how long each stage takes, what you'll need to send, and when the money actually hits your account.",
    date: "2026-01-18",
    readTime: "6 min read",
    category: "Playbook",
    image:
      "https://images.pexels.com/photos/12471781/pexels-photo-12471781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  body: `
## The moment after pre-qualification

You submitted an application. You got a callback within an hour. The broker or lender told you "you pre-qualify for $X at approximately Y% — let's move forward." Now what?

For a lot of business owners, this is where the process feels murky. Between "you pre-qualify" and "here's the wire confirmation," there are 5 to 10 real steps — and how quickly you complete them determines whether you fund this week or three weeks from now.

Here's exactly what happens next.

## Step 1: Soft offer review (Day 0)

**What it is:** A written document showing your pre-approval amount, estimated rate, term length, and expected monthly payment. This is not a formal offer yet — it's a "based on what we know so far, here's what you can expect."

**What to check:**
- Is the amount what you actually need?
- Is the term what you asked for?
- Is the estimated APR (not just factor rate — insist on APR) in the range you expected?
- Any fees? Origination, processing, or documentation fees should be listed here, not surprise-dropped at closing.

**Time to complete:** 5–15 minutes to review.

**Broker's job:** Answer every question you have on this document, in writing. If you're getting rushed past questions, that's a red flag.

## Step 2: Documentation request (Day 0–1)

**What it is:** The lender asks for a specific set of documents so a real underwriter can verify what your application claimed. For most fintech products this is minimal; for SBA and bank term loans it's substantial.

**Fintech / MCA / Revenue product documentation:**
- ID (driver's license)
- Voided check from the business operating account
- Last 4–6 months of business bank statements (or a Plaid link)
- Signed application form
- Sometimes: proof of business ownership (Articles of Organization, EIN letter)

**Time to funding from here:** 24–72 hours if you send everything today.

**SBA / bank term loan documentation:**
- 2 years of business tax returns
- 2 years of personal tax returns
- Interim P&L and balance sheet (year-to-date)
- Business debt schedule
- Personal financial statement (SBA Form 413)
- Articles of organization + operating agreement
- Business licenses
- Sometimes: business plan, projections, use-of-funds breakdown

**Time to funding from here:** 30–60 days.

**Speed tip:** the difference between funding on Friday and funding three weeks from now often comes down to how quickly you send documents. If you have everything ready when you submit, we can fund in 2–3 days on most fintech products.

## Step 3: Underwriting (Day 1–5)

**What it is:** The lender's underwriting team (human, algorithm, or both) reviews your documents against their credit policy. They're verifying that your bank statements show what your application claimed, that your credit report matches expectations, and that no red flags surface.

**What can slow this down:**
- Bank statement inconsistencies (deposits don't match claimed revenue)
- Existing loans/MCAs not disclosed on the application
- Recent negative days on your statements
- Tax liens or judgments not disclosed
- Missing documents

**What can speed this up:**
- Preemptively explaining anything unusual in your file
- Filing recent tax returns promptly
- Being reachable when the underwriter has a question

**Timing:** 4–24 hours for fintech; 5–15 business days for bank/SBA.

## Step 4: Formal offer (Day 1–7)

**What it is:** The final, binding offer document. Now you're seeing the *real* numbers, not estimates:
- Exact loan amount (may be higher or lower than the soft offer based on what underwriting found)
- Exact rate/factor
- Exact term
- Exact payment schedule
- Any fees, itemized
- Any conditions (personal guarantee, collateral, covenants)

**What happens now:** You have a decision window. For most fintech products, offers are valid for 3–7 days. For SBA/bank offers, sometimes 30 days.

**What triggers the first hard credit pull:** if you accept and sign the offer, the lender will now run a **hard credit inquiry** to confirm your credit for closing. This is the only inquiry that appears on your report — one lender, one pull, one time.

**Your call:** accept, negotiate, or walk. A broker who tries to hard-close you in the first 24 hours is pressuring you; a broker who walks you through every line and gives you 48 hours to consult your accountant is doing their job.

## Step 5: Contract signing (Day 2–14)

**What it is:** The legal agreement. For fintech products this is a 15–25 page e-signed document; for SBA it's a 40–80 page package with in-person or notarized signatures.

**What to actually read** (yes, all of it, but especially these):
- **The payment schedule** — exact dollar amounts, exact frequency
- **The prepayment clause** — is there a real discount for early payoff?
- **The personal guarantee scope** — you personally? Your spouse? Only in event of default, or unconditional?
- **The reconciliation clause** (for MCA/revenue products) — can the daily debit be adjusted down if revenue slows?
- **Any covenants** (bank/SBA) — restrictions on additional debt, distributions, or changes in ownership

**Timing:** 20 minutes to 2 hours to sign, depending on product.

## Step 6: Funding (Day 3–15)

**What it is:** The lender wires (or ACHs) the funds to your business operating account.

**Fintech / MCA / Revenue:** ACH deposit lands in 1–3 business days after signing. Some funders offer same-day wire for a small fee ($25–$50).

**Bank term loan:** Wire lands 1–3 business days after signing, sometimes same-day.

**SBA:** Depending on the specific structure (7(a) vs 504) and whether real estate is involved, funding can take another 5–30 days after signing, sometimes with a second closing for CDC portion on 504 deals.

## Step 7: The first payment (Day 3–35)

The first ACH debit or invoice arrives on the schedule you signed. For MCA/revenue products, the first debit is often within 3 business days of funding. For term loans, the first payment is typically 30 days after funding.

**Rule of thumb:** don't spend the entire loan amount in the first week — leave enough in the account to cover the first 30 days of payments comfortably. Nothing kills a new funded loan faster than an overdraft on the first debit.

## The realistic timeline, end to end

| Product | Application → Funding |
|---|---|
| **Merchant Cash Advance** | 24–72 hours (same-day possible) |
| **Revenue-based financing** | 1–3 business days |
| **Fintech term loan** | 2–5 business days |
| **Fintech line of credit** | 3–7 business days |
| **Equipment financing** | 5–14 business days |
| **Bank line of credit** | 2–4 weeks |
| **SBA 7(a)** | 30–60 days |
| **SBA 504** | 60–90 days (two closings) |

## What a good broker actually does through all this

- Explains the difference between a soft offer and a formal offer
- Translates factor rates to APR before you sign
- Reviews the contract line-by-line and flags anything unusual
- Negotiates when there's room to negotiate (there usually is on rate, term length, or fees)
- Follows up with the lender when documentation goes into a black hole
- Reminds you what documents you'll need for a follow-up loan in year 2

If your broker disappears between pre-qualification and funding, or only calls you back when something needs signing — you're being handled by someone whose commission is more important than your loan.

At Steady Path, we stay with you through every step and every product. [Apply here](/apply) to see what's possible.

Related: [How to prepare for a business loan application](/blog/prepare-business-loan-application-2026) · [Does checking your rate hurt your credit?](/blog/does-checking-your-rate-hurt-your-credit)
`,
};

export default post;
